// Implements: F-CNV-008, F-UIX-009, F-UIX-010, F-UIX-018
/**
 * The offer to run the last unfinished turn again. Pure presentation.
 *
 * It shows only the action and the server's own refusal sentence when there was one: it never
 * says whether the retry is free or charged (OD-12). Which turn is retried, and whether it can
 * be at all, is decided by the container from the conversation state.
 *
 * A usage refusal (UIX.2) is not something trying again changes: only the month turning or an
 * administrator does. The bar then keeps the sentence of its reason and loses the button and
 * the hint, so nothing on screen invites repeating it.
 */

import type { ReactElement } from "react";

import { useTranslation } from "../../../shared/i18n";
import { Button } from "../../../shared/ui/atoms/Button";
import { USAGE_LIMIT_REACHED } from "../../../shared/ui/molecules/usageRefusal";
import { TURN_REFUSAL_CODES, userMessage } from "../../../shared/ui/molecules/userMessage";
import type { ApiFailure } from "../../../types/errors";
import styles from "./RetryBar.module.css";

export interface RetryBarProps {
  readonly onRetry: () => void;
  /** The refusal of the last attempt, if any. */
  readonly error: ApiFailure | null;
}

export function RetryBar({ onRetry, error }: RetryBarProps): ReactElement {
  const { t } = useTranslation();
  const usageRefused = error?.code === USAGE_LIMIT_REACHED;
  return (
    <div className={styles.bar}>
      {usageRefused ? null : (
        <>
          <p className={styles.hint}>{t("conversation.retry.hint")}</p>
          <Button variant="secondary" size="sm" onClick={onRetry}>
            {t("conversation.retry.action")}
          </Button>
        </>
      )}
      {error === null ? null : (
        <p className={styles.error} role="alert">
          {userMessage(error, t("conversation.retry.failed"), TURN_REFUSAL_CODES)}
        </p>
      )}
    </div>
  );
}
